import Markdown from "markdown-to-jsx";
import React, { useState, useCallback, memo, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  ProgressBar,
  Badge,
  InputGroup,
  Breadcrumb,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronRight,
  faDollarSign,
  faCalendarAlt,
  faUsers,
  faMapMarkerAlt,
  faClock,
  faChevronLeft,
  faHistory,
  faFileDownload,
} from "@fortawesome/free-solid-svg-icons";
import { useRouteMatch, useHistory, Route, Switch } from "react-router-dom";
import { downloadBudgetById, generateBudget } from "../../../service/workflow-api";
import SavedBudgets from "./saved-budgets";
import {
  STEPS,
  STEP_LABELS,
  ROLES,
  BUDGET_CATEGORIES,
  EVENT_TYPES,
  EVENT_SCALES,
  LOCATION_TYPES,
  INITIAL_FORM_STATE,
} from "./data";
import "./BudgetPlanner.css";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

const RoleSelector = memo(({ selectedRole, onRoleSelect }) => (
  <Row>
    {ROLES.map((role) => (
      <Col md={6} key={role} className="mb-3">
        <Card
          className={`cursor-pointer dark-card ${
            selectedRole === role ? "border-primary" : ""
          }`}
          onClick={() => onRoleSelect(role)}
        >
          <Card.Body className="d-flex justify-content-between align-items-center">
            <span className="text-white">{role}</span>
            {selectedRole === role && (
              <FontAwesomeIcon icon={faCheck} className="text-primary" />
            )}
          </Card.Body>
        </Card>
      </Col>
    ))}
  </Row>
));

const CategorySelector = memo(
  ({ categories, priorities, onToggleCategory, onUpdatePriority }) => (
    <>
      {BUDGET_CATEGORIES.map((category) => (
        <Card key={category} className="mb-3 dark-card">
          <Card.Body>
            <Form.Check
              type="checkbox"
              id={`category-${category}`}
              label={category}
              checked={categories.includes(category)}
              onChange={() => onToggleCategory(category)}
              className="mb-2 text-light dark-checkbox"
            />
            {categories.includes(category) && (
              <div className="mt-3 pl-4">
                <Form.Label className="text-muted">
                  Priority (1 = Low, 5 = High): {priorities[category] || 3}
                </Form.Label>
                <Form.Range
                  min="1"
                  max="5"
                  value={priorities[category] || 3}
                  onChange={(e) =>
                    onUpdatePriority(category, parseInt(e.target.value))
                  }
                  className="dark-range"
                />
                <div className="d-flex justify-content-between">
                  <small className="text-muted">1</small>
                  <small className="text-muted">2</small>
                  <small className="text-muted">3</small>
                  <small className="text-muted">4</small>
                  <small className="text-muted">5</small>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      ))}
    </>
  )
);

const BudgetPlanner = () => {
  const [formData, setFormData] = useState({
    eventName: "",
    eventType: "",
    eventScale: "",
    duration: "",
    locationType: "",
    currencyType: "US Dollar",
    totalBudget: "",
    primaryRole: "",
    categories: [],
    categoryPriorities: {},
    budgetBreakdown: null,
    budgetId: null,
    venueDetails: {
      venueType: "",
      rentalDuration: "",
      setupTeardown: "",
      specialRequirements: "",
    },
    productionDetails: {
      lightingNeeds: "",
      soundNeeds: "",
      visualDisplays: [],
    },
    customRequirements: "",
  });
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { path, url } = useRouteMatch();
  const history = useHistory();

  const downloadBudget = async (id) => {
    try {
        const response = await downloadBudgetById(id); // Assuming this fetches the file from the server.

        // Check the response status before proceeding.
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Create a link element and trigger download
        const blob = await response.blob(); // Use blob() to get the response as a Blob
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Budget-${new Date().toString()}.xlsx`); // Use template literals correctly
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Error downloading budget:", error);
        // Handle the error appropriately, e.g., display a message to the user.
        alert("Failed to download the budget. Please try again later."); // Example user feedback
    }
};

  const handleNext = useCallback(async () => {
    if (step === STEPS.TIMELINE) {
      setLoading(true);
      setError(null);
      console.log(formData)
      try {
        console.log(formData);
        const response = await generateBudget({
          "form": formData,
        });

        setFormData(prev => ({
          ...prev,
          budgetBreakdown: response.brief
        }));
        setFormData(prev => ({
          ...prev,
          budgetId: response.budget_id,
        }))
        setStep((prev) => prev + 1);
      } catch (err) {
        setError("Failed to generate budget. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      setStep((prev) => prev + 1);
    }
  }, [step, formData]);

  const handleBack = useCallback(() => {
    setStep((prev) => prev - 1);
  }, []);

  const toggleCategory = useCallback((category) => {
    setFormData((prev) => {
      const currentCategories = [...prev.categories];
      if (currentCategories.includes(category)) {
        return {
          ...prev,
          categories: currentCategories.filter((c) => c !== category),
        };
      }
      return {
        ...prev,
        categories: [...currentCategories, category],
      };
    });
  }, []);

  const updatePriority = useCallback((category, value) => {
    setFormData((prev) => ({
      ...prev,
      categoryPriorities: {
        ...prev.categoryPriorities,
        [category]: value,
      },
    }));
  }, []);

  const navigateToHistory = useCallback(() => {
    history.push(`${url}/saved`);
  }, [history, url]);

  const navigateToBudgetPlanner = useCallback(() => {
    history.push(url);
  }, [history, url]);

  const renderProgress = useCallback(
    () => (
      <div className="mb-4">
        <Row className="text-center mb-2">
          {STEP_LABELS.map((label, index) => (
            <Col key={index}>
              <div
                className={`rounded-circle d-inline-flex align-items-center justify-content-center ${
                  step > index + 1
                    ? "bg-primary text-white"
                    : step === index + 1
                    ? "bg-dark-accent text-primary border border-primary"
                    : "bg-dark-accent text-muted"
                }`}
                style={{ width: "40px", height: "40px" }}
              >
                {step > index + 1 ? (
                  <FontAwesomeIcon icon={faCheck} size="sm" />
                ) : (
                  index + 1
                )}
              </div>
              <div className="mt-1">
                <small
                  className={
                    step >= index + 1
                      ? "text-primary font-weight-bold"
                      : "text-white"
                  }
                >
                  {label}
                </small>
              </div>
            </Col>
          ))}
        </Row>
        <ProgressBar now={(step / 6) * 100} variant="primary" />
      </div>
    ),
    [step]
  );

  const location = useLocation();
  const inputRef = useRef(null);

  const updateForm = useCallback((field, value) => {
    setFormData((prev) => {
      const newFormData = { ...prev, [field]: value };
      return newFormData;
    });
  }, []);

  const updateVenueDetails = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      venueDetails: {
        ...prev.venueDetails,
        [field]: value,
      },
    }));
  }, []);

  const updateProductionDetails = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      productionDetails: {
        ...prev.productionDetails,
        [field]: value,
      },
    }));
  }, []);

  const toggleVisualDisplay = useCallback((display) => {
    setFormData((prev) => {
      const currentDisplays = [...prev.productionDetails.visualDisplays];
      if (currentDisplays.includes(display)) {
        return {
          ...prev,
          productionDetails: {
            ...prev.productionDetails,
            visualDisplays: currentDisplays.filter((d) => d !== display),
          },
        };
      }
      return {
        ...prev,
        productionDetails: {
          ...prev.productionDetails,
          visualDisplays: [...currentDisplays, display],
        },
      };
    });
  }, []);

  return (
    <Switch>
      <Route exact path={path}>
        <>
          <Breadcrumb className="dark-breadcrumb mb-4">
            <Breadcrumb.Item href="/ai-workflows">AI Workflows</Breadcrumb.Item>
            <Breadcrumb.Item active>Budget Planner</Breadcrumb.Item>
          </Breadcrumb>

          <Container fluid className="py-1 budget-planner-container">
            <Card className="shadow dark-main-card">
              <Card.Body className="p-4">
                <div className="mb-5 text-center">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <p
                      className="display-5 mb-0 text-primary cursor-pointer"
                      style={{
                        fontWeight: 200,
                        textShadow: "0 0 10px rgba(79, 209, 197, 0.3)",
                        fontSize: "1.8rem",
                      }}
                    >
                      AI Budget Planner
                    </p>
                    <Button
                      variant="outline-primary"
                      className="dark-outline-button"
                      onClick={navigateToHistory}
                    >
                      <FontAwesomeIcon icon={faHistory} className="me-2" />
                      History
                    </Button>
                  </div>
                  {renderProgress()}
                </div>

                {error && (
                  <div className="alert dark-alert alert-danger mb-4">
                    {error}
                  </div>
                )}

                {loading && (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="text-light mt-3">
                      Generating your budget plan...
                    </p>
                  </div>
                )}

                {step === STEPS.EVENT_DETAILS ? (
                  <div className="mb-4">
                    <h2 className="mb-4 text-white">Event Details</h2>
                    <Form>
                      <Form.Group className="mb-3" controlId="eventName">
                        <Form.Label className="text-white">
                          Event Name
                        </Form.Label>
                        <Form.Control
                          ref={inputRef}
                          type="text"
                          required
                          name="eventName"
                          value={formData.eventName}
                          onChange={(e) =>
                            updateForm("eventName", e.target.value)
                          }
                          placeholder="Annual Tech Conference 2025"
                          className="dark-input"
                        />
                      </Form.Group>

                      <Form.Group className="mb-3" controlId="eventType">
                        <Form.Label className="text-white">
                          Event Type
                        </Form.Label>
                        <Form.Select
                          name="eventType"
                          value={formData.eventType || ""}
                          onChange={(e) =>
                            updateForm("eventType", e.target.value)
                          }
                          className="dark-input"
                        >
                          <option value="">Select event type</option>
                          {EVENT_TYPES.map(({ value, label }) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-3" controlId="currencyType">
                        <Form.Label className="text-white">Currency</Form.Label>
                        <Form.Select
                          name="currencyType"
                          value={formData.currencyType || ""}
                          onChange={(e) =>
                            updateForm("currencyType", e.target.value)
                          }
                          className="dark-input"
                        >
                          <option value="">Select currency</option>
                          <option value="USD">US Dollar</option>
                          <option value="EUR">Euro</option>
                          <option value="GBP">British Pound Sterling</option>
                        </Form.Select>
                      </Form.Group>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3" controlId="eventScale">
                            <Form.Label className="text-white">
                              Event Scale
                            </Form.Label>
                            <Form.Select
                              name="eventScale"
                              required
                              value={formData.eventScale || ""}
                              onChange={(e) =>
                                updateForm("eventScale", e.target.value)
                              }
                              className="dark-input"
                            >
                              <option value="">Select scale</option>
                              {EVENT_SCALES.map(({ value, label }) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3" controlId="duration">
                            <Form.Label className="text-white">
                              Duration (days)
                            </Form.Label>
                            <Form.Control
                              type="number"
                              name="duration"
                              value={formData.duration || ""}
                              onChange={(e) =>
                                updateForm("duration", e.target.value)
                              }
                              min="1"
                              placeholder="3"
                              className="dark-input"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3" controlId="locationType">
                            <Form.Label className="text-white">
                              Location Type
                            </Form.Label>
                            <Form.Select
                              name="locationType"
                              value={formData.locationType || ""}
                              onChange={(e) =>
                                updateForm("locationType", e.target.value)
                              }
                              className="dark-input"
                            >
                              {LOCATION_TYPES.map(({ value, label }) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3" controlId="totalBudget">
                            <Form.Label className="text-white">
                              Total Budget (Optional)
                            </Form.Label>
                            <InputGroup>
                              <InputGroup.Text className="dark-input-group-text">
                                {
                                  <span>
                                    {formData.currencyType === "EUR"
                                      ? "€"
                                      : formData.currencyType === "GBP"
                                      ? "£"
                                      : "$"}
                                  </span>
                                }
                              </InputGroup.Text>
                              <Form.Control
                                type="number"
                                required
                                name="totalBudget"
                                value={formData.totalBudget || ""}
                                onChange={(e) =>
                                  updateForm("totalBudget", e.target.value)
                                }
                                placeholder="50000"
                                className="dark-input"
                              />
                            </InputGroup>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Form>
                  </div>
                ) : step === STEPS.ROLE ? (
                  <div className="mb-4">
                    <h2 className="mb-4 text-white">Your Role</h2>
                    <p className="text-white mb-4">
                      Select your primary role for this event. This helps us
                      show relevant budget categories.
                    </p>
                    <RoleSelector
                      selectedRole={formData.primaryRole}
                      onRoleSelect={(role) => updateForm("primaryRole", role)}
                    />
                  </div>
                ) : step === STEPS.CATEGORIES ? (
                  <div className="mb-4">
                    <h2 className="mb-4 text-white">Budget Categories</h2>
                    <p className="text-white mb-4">
                      Select all budget categories relevant to your event and
                      set their priority level.
                    </p>
                    <CategorySelector
                      categories={formData.categories}
                      priorities={formData.categoryPriorities}
                      onToggleCategory={toggleCategory}
                      onUpdatePriority={updatePriority}
                    />
                  </div>
                ) : step === STEPS.DETAILS ? (
                  <div className="mb-4">
                    <h2 className="mb-4 text-white">Additional Details</h2>
                    <p className="text-white mb-4">
                      Provide details for each selected category to get accurate
                      budget estimates.
                    </p>
                    {formData.categories.includes("Venue & Space") && (
                      <Card className="mb-4 dark-card">
                        <Card.Header className="dark-card-header">
                          <h5 className="mb-0 text-light">Venue & Space</h5>
                        </Card.Header>
                        <Card.Body>
                          <Form>
                            <Form.Group className="mb-3" controlId="venueType">
                              <Form.Label className="text-light">
                                Venue Type
                              </Form.Label>
                              <Form.Select
                                className="dark-input"
                                value={formData.venueDetails.venueType}
                                onChange={(e) =>
                                  updateVenueDetails(
                                    "venueType",
                                    e.target.value
                                  )
                                }
                                tubo
                              >
                                <option value="">Select venue type</option>
                                <option value="hotel">Hotel/Resort</option>
                                <option value="conference">
                                  Conference Center
                                </option>
                                <option value="theater">Theater</option>
                                <option value="studio">Studio</option>
                                <option value="outdoor">Outdoor/Park</option>
                                <option value="arena">Arena/Stadium</option>
                                <option value="museum">Museum/Gallery</option>
                              </Form.Select>
                            </Form.Group>
                            <Row>
                              <Col md={6}>
                                <Form.Group
                                  className="mb-3"
                                  controlId="rentalDuration"
                                >
                                  <Form.Label className="text-light">
                                    Rental Duration (days)
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    min="1"
                                    placeholder="3"
                                    className="dark-input"
                                    value={formData.venueDetails.rentalDuration}
                                    onChange={(e) =>
                                      updateVenueDetails(
                                        "rentalDuration",
                                        e.target.value
                                      )
                                    }
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group
                                  className="mb-3"
                                  controlId="setupTeardown"
                                >
                                  <Form.Label className="text-light">
                                    Setup/Teardown (days)
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    min="0"
                                    placeholder="1"
                                    className="dark-input"
                                    value={formData.venueDetails.setupTeardown}
                                    onChange={(e) =>
                                      updateVenueDetails(
                                        "setupTeardown",
                                        e.target.value
                                      )
                                    }
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                            <Form.Group
                              className="mb-3"
                              controlId="specialRequirements"
                            >
                              <Form.Label className="text-light">
                                Special Requirements
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Stage size, green rooms, A/V capabilities, etc."
                                className="dark-input"
                                value={
                                  formData.venueDetails.specialRequirements
                                }
                                onChange={(e) =>
                                  updateVenueDetails(
                                    "specialRequirements",
                                    e.target.value
                                  )
                                }
                              />
                            </Form.Group>
                          </Form>
                        </Card.Body>
                      </Card>
                    )}
                    {formData.categories.includes("Production & Technical") && (
                      <Card className="mb-4 dark-card">
                        <Card.Header className="dark-card-header">
                          <h5 className="mb-0 text-light">
                            Production & Technical
                          </h5>
                        </Card.Header>
                        <Card.Body>
                          <Form>
                            <Row>
                              <Col md={6}>
                                <Form.Group
                                  className="mb-3"
                                  controlId="lightingNeeds"
                                >
                                  <Form.Label className="text-light">
                                    Lighting Needs
                                  </Form.Label>
                                  <Form.Select
                                    className="dark-input"
                                    value={
                                      formData.productionDetails.lightingNeeds
                                    }
                                    onChange={(e) =>
                                      updateProductionDetails(
                                        "lightingNeeds",
                                        e.target.value
                                      )
                                    }
                                  >
                                    <option value="">Select level</option>
                                    <option value="basic">Basic</option>
                                    <option value="standard">Standard</option>
                                    <option value="premium">Premium</option>
                                    <option value="custom">Custom</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group
                                  className="mb-3"
                                  controlId="soundNeeds"
                                >
                                  <Form.Label className="text-light">
                                    Sound Needs
                                  </Form.Label>
                                  <Form.Select
                                    className="dark-input"
                                    value={
                                      formData.productionDetails.soundNeeds
                                    }
                                    onChange={(e) =>
                                      updateProductionDetails(
                                        "soundNeeds",
                                        e.target.value
                                      )
                                    }
                                  >
                                    <option value="">Select level</option>
                                    <option value="basic">Basic</option>
                                    <option value="standard">Standard</option>
                                    <option value="premium">Premium</option>
                                    <option value="custom">Custom</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                            </Row>
                            <Form.Group
                              className="mb-3"
                              controlId="visualDisplayNeeds"
                            >
                              <Form.Label className="text-light">
                                Visual Display Needs
                              </Form.Label>
                              <div>
                                <Form.Check
                                  inline
                                  type="checkbox"
                                  id="projection"
                                  label="Projection"
                                  className="text-light dark-checkbox"
                                  checked={formData.productionDetails.visualDisplays.includes(
                                    "projection"
                                  )}
                                  onChange={() =>
                                    toggleVisualDisplay("projection")
                                  }
                                />
                                <Form.Check
                                  inline
                                  type="checkbox"
                                  id="led"
                                  label="LED Walls"
                                  className="text-light dark-checkbox"
                                  checked={formData.productionDetails.visualDisplays.includes(
                                    "led"
                                  )}
                                  onChange={() => toggleVisualDisplay("led")}
                                />
                                <Form.Check
                                  inline
                                  type="checkbox"
                                  id="monitors"
                                  label="Monitors"
                                  className="text-light dark-checkbox"
                                  checked={formData.productionDetails.visualDisplays.includes(
                                    "monitors"
                                  )}
                                  onChange={() =>
                                    toggleVisualDisplay("monitors")
                                  }
                                />
                                <Form.Check
                                  inline
                                  type="checkbox"
                                  id="streaming"
                                  label="Streaming"
                                  className="text-light dark-checkbox"
                                  checked={formData.productionDetails.visualDisplays.includes(
                                    "streaming"
                                  )}
                                  onChange={() =>
                                    toggleVisualDisplay("streaming")
                                  }
                                />
                              </div>
                            </Form.Group>
                          </Form>
                        </Card.Body>
                      </Card>
                    )}
                    <Form.Group className="mb-3" controlId="customInput">
                      <Form.Label className="text-light">
                        Custom Input
                      </Form.Label>
                      <Form.Control
                        type="text"
                        as={"textarea"}
                        rows={4}
                        placeholder="Add your custom requirements for the budget"
                        className="dark-input"
                        value={formData.customRequirements}
                        onChange={(e) =>
                          updateForm("customRequirements", e.target.value)
                        }
                      />
                    </Form.Group>
                    {formData.categories.filter(
                      (c) =>
                        !["Venue & Space", "Production & Technical"].includes(c)
                    ).length > 0 && (
                      <Card className="mb-4 dark-card-alt">
                        <Card.Body className="text-center">
                          <p className="text-muted">
                            Additional category inputs would appear here based
                            on selections
                          </p>
                        </Card.Body>
                      </Card>
                    )}
                  </div>
                ) : step === STEPS.TIMELINE ? (
                  <div className="mb-4">
                    <h2 className="mb-4 text-white">Timeline & Cash Flow</h2>
                    <Form>
                      <Form.Group className="mb-3" controlId="eventDate">
                        <Form.Label className="text-light">
                          Event Date
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="dark-input-group-text">
                            <FontAwesomeIcon icon={faCalendarAlt} />
                          </InputGroup.Text>
                          <Form.Control type="date" className="dark-input" />
                        </InputGroup>
                      </Form.Group>
                      <Form.Group className="mb-3" controlId="planningTimeline">
                        <Form.Label className="text-light">
                          Planning Timeline
                        </Form.Label>
                        <Form.Select className="dark-input">
                          <option value="">Select timeline</option>
                          <option value="1month">1 month before event</option>
                          <option value="3months">3 months before event</option>
                          <option value="6months">6 months before event</option>
                          <option value="1year">1 year before event</option>
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-3" controlId="depositSchedule">
                        <Form.Label className="text-light">
                          Deposit Schedule Preference
                        </Form.Label>
                        <Form.Select className="dark-input">
                          <option value="">Select preference</option>
                          <option value="upfront">
                            High upfront, low later (50/30/20)
                          </option>
                          <option value="even">
                            Even distribution (33/33/34)
                          </option>
                          <option value="backloaded">
                            Low upfront, high later (20/30/50)
                          </option>
                        </Form.Select>
                      </Form.Group>
                    </Form>
                  </div>
                ) : step === STEPS.BUDGET ? (
                  <div className="mb-4">
                    <div className="alert dark-alert mb-4">
                      <span>
                        <Markdown>{formData.budgetBreakdown}</Markdown>
                      </span>
                    </div>
                    <Card className="mb-4 dark-card">
                      <Card.Header className="dark-card-header">
                        <div className="d-flex justify-content-between align-items-center">
                          <h4 className="mb-0 text-light">
                            {formData.eventName || "Event Budget"}
                          </h4>
                          <Badge bg="success" className="p-2">
                            <FontAwesomeIcon
                              icon={faDollarSign}
                              className="me-1"
                            />
                            {formData.totalBudget} Total
                          </Badge>
                        </div>
                        <Row className="mt-3">
                          <Col md={4} className="d-flex align-items-center">
                            <FontAwesomeIcon
                              icon={faCalendarAlt}
                              className="text-muted me-2"
                            />
                            <span className="text-light">
                              {formData.duration || "3"} days
                            </span>
                          </Col>
                          <Col md={4} className="d-flex align-items-center">
                            <FontAwesomeIcon
                              icon={faMapMarkerAlt}
                              className="text-muted me-2"
                            />
                            <span className="text-light">
                              {formData.locationType || "Urban"}
                            </span>
                          </Col>
                        </Row>
                      </Card.Header>
                      <Card.Body>
                      <div className="d-flex justify-content-between align-items-center">
                      <Button variant="link" className="text-primary" onClick={() => setStep(4)}>
                        Edit Budget
                      </Button>
                      <div>
                        <Button
                          variant="primary"
                          className="me-2 dark-primary-button"
                          onClick={() => downloadBudget(formData.budgetId)}
                        >
                          <FontAwesomeIcon
                            icon={faFileDownload}
                            className="me-1"
                          />
                          Detailed Excel
                        </Button>
                      </div>
                    </div>
                      </Card.Body>
                    </Card>
                    
                  </div>
                ) : null}

                <hr className="dark-divider" />

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={handleBack}
                    disabled={step === STEPS.EVENT_DETAILS || loading}
                    className="dark-outline-button"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleNext}
                    disabled={step === STEPS.BUDGET || loading}
                    className="dark-primary-button"
                  >
                    {step < STEPS.BUDGET ? (
                      <>
                        Next
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="ms-1"
                        />
                      </>
                    ) : (
                      "Finish"
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Container>
        </>
      </Route>
      <Route path={`${path}/saved`}>
        <SavedBudgets onBack={navigateToBudgetPlanner} />
      </Route>
    </Switch>
  );
};

export default BudgetPlanner;
